import React from "react";
import { io } from "socket.io-client";
import { getCurrentIdToken } from "../../../firebase/firebaseClient";

// The rider socket the backend mounts for one hail (backend-functions realtime-keys.ts
// HAIL_REALTIME_PATH). Same connection as qa-api-tools' socket-tester rider lane.
export const HAIL_REALTIME_PATH = "/hailing-v2/realtime/hail";
// Every frame, status or driver pin, arrives on this one socket.io event.
export const HAIL_MESSAGE_EVENT = "message";
export const DRIVER_HEARTBEAT_CODE = "DRIVER_HEARTBEAT";

export interface HailSocketFrame {
  message: string;
  payload?: unknown;
}

export interface DriverHeartbeat {
  lat: number;
  lng: number;
}

export interface UseHailRealtimeOptions {
  /** A hail frame other than the driver pin: the order changed, so read it again. */
  onOrderChanged: (code: string) => void;
  /** The driver's latest position (`DRIVER_HEARTBEAT`). */
  onDriverHeartbeat: (heartbeat: DriverHeartbeat) => void;
}

// The socket server shares the API host; only the path differs.
const socketOrigin = (): string | null => {
  const endpoint = process.env.REACT_APP_api_endpoint;
  if (!endpoint) return null;
  try {
    return new URL(endpoint).origin;
  } catch {
    return null;
  }
};

const isHeartbeat = (payload: unknown): payload is DriverHeartbeat =>
  typeof payload === "object" &&
  payload !== null &&
  typeof (payload as DriverHeartbeat).lat === "number" &&
  typeof (payload as DriverHeartbeat).lng === "number";

/**
 * Live updates for one hail over the rider socket. Returns whether the socket is connected, so
 * the caller can poll slowly while it is and fall back to its normal polling while it is not.
 */
export const useHailRealtime = (
  hailId: string | null | undefined,
  options: UseHailRealtimeOptions,
): boolean => {
  const [connected, setConnected] = React.useState(false);
  const optionsRef = React.useRef(options);
  optionsRef.current = options;

  React.useEffect(() => {
    const origin = socketOrigin();
    if (!hailId || !origin) return;

    const socket = io(origin, {
      path: HAIL_REALTIME_PATH,
      transports: ["websocket"],
      // A function, so every reconnect sends a fresh ID token. The hail id must ride `auth`:
      // the server reads `handshake.auth.hailId`, not a query parameter.
      auth: (cb) => {
        getCurrentIdToken()
          .then((token) => cb({ token: token ?? "", hailId }))
          .catch(() => cb({ token: "", hailId }));
      },
      reconnectionDelay: 2_000,
      reconnectionDelayMax: 30_000,
    });

    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    // A refused handshake (not this rider's hail, or the hail already ended) is not retried by
    // socket.io; the caller's polling carries on.
    socket.on("connect_error", () => setConnected(false));
    socket.on(HAIL_MESSAGE_EVENT, (frame: HailSocketFrame) => {
      if (!frame?.message) return;
      if (frame.message === DRIVER_HEARTBEAT_CODE) {
        if (isHeartbeat(frame.payload)) {
          optionsRef.current.onDriverHeartbeat({ lat: frame.payload.lat, lng: frame.payload.lng });
        }
        return;
      }
      optionsRef.current.onOrderChanged(frame.message);
    });

    return () => {
      socket.disconnect();
      setConnected(false);
    };
  }, [hailId]);

  return connected;
};
